import { Box, Drawer, IconButton, Typography } from '@mui/material';
import { useMemo, useState, MouseEvent, useEffect } from 'react';
import { Thumbnail } from './Inclusions/modal';
import { ProductApiData } from '@/services/catalogue/models/pdp';
import ImageThumbnailSelector from './ImageCarousel/ImageThumbnailSelector';
import FlexBox from '@/atomic-components/FlexBox';
import { getStyle } from '@/utils/theme/fonts';
import { getColor } from '@/utils/theme/colors';
import CloseIcon from '@/assets/CloseIcon.svg';

type ImageGalleryProps = {
	open: boolean;
	onClose: () => void;
	defaultImage?: Thumbnail;
	images: Thumbnail[];
	productDetails: ProductApiData;
};

type CursorPointer = {
	cursorXPosition: number;
	cursorYPosition: number;
};

function getCursorPositionRelativeToImage(
	e: MouseEvent,
	img: HTMLCanvasElement
): CursorPointer {
	var a,
		x = 0,
		y = 0;
	e = e || window.event;
	/* Get the x and y positions of the image: */
	a = img.getBoundingClientRect();
	/* Calculate the cursor's x and y coordinates, relative to the image: */
	x = e.pageX - a.left;
	y = e.pageY - a.top;
	/* Consider any page scrolling: */
	x = x - window.pageXOffset;
	y = y - window.pageYOffset;
	return { cursorXPosition: x, cursorYPosition: y };
}

const ImageGalleryDrawer = (props: ImageGalleryProps) => {
	const [selectedImage, setSelectedImage] = useState<Thumbnail | undefined>(
		props.defaultImage ?? props.images[0]
	);
	const [isMagnifiedViewVisible, setIsMagnifiedViewVisible] = useState(false);

	let zoom = 3;

	const currentSlide = useMemo(
		() => props.images.findIndex((image) => image.url === selectedImage?.url),
		[selectedImage?.url]
	);

	const glass = document.getElementById('glass') as HTMLCanvasElement;
	const img = document.getElementById('image-to-zoom') as HTMLCanvasElement;

	function onImageSelection(image: Thumbnail, index: number) {
		setSelectedImage(image);
	}

	function setPositionOfMagnifierGlass(
		x: number,
		y: number,
		h: number,
		w: number
	) {
		if (glass?.style) {
			glass.style.left = x - w + 'px';
			glass.style.top = y - h + 'px';
			glass.style.backgroundImage = `url(${selectedImage?.url})`;
			/*display what the magnifier glass "sees":*/
			glass.style.backgroundPosition =
				'-' + (x * zoom - w) + 'px -' + (y * zoom - h) + 'px';
		}
	}

	function getAdjustedPositionForMagnifierGlass(
		cursorPosition: CursorPointer,
		h: number,
		w: number
	) {
		/*prevent the magnifier glass from being positioned outside the image:*/
		let x = cursorPosition.cursorXPosition,
			y = cursorPosition.cursorYPosition;

		if (x > img.width - w / zoom) {
			x = img.width - w / zoom;
		}
		if (x < w / zoom) {
			x = w / zoom;
		}
		if (y > img.height - h / zoom) {
			y = img.height - h / zoom;
		}
		if (y < h / zoom) {
			y = h / zoom;
		}

		return {
			x,
			y,
		};
	}

	function onHoverOverImage(e: MouseEvent<HTMLImageElement>) {
		/*prevent any other actions that may occur when moving over the image*/
		e.preventDefault();

		let w = (glass.offsetHeight ?? 0) / 2;
		let h = (glass.offsetHeight ?? 0) / 2;

		const cursorPosition = getCursorPositionRelativeToImage(e, img);

		const { x, y } = getAdjustedPositionForMagnifierGlass(cursorPosition, h, w);

		setPositionOfMagnifierGlass(x, y, h, w);
		setIsMagnifiedViewVisible(true);
	}

	useEffect(() => {
		setSelectedImage(props.defaultImage);
	}, [props.defaultImage?.url]);

	if (!props.productDetails) return null;

	return (
		<Drawer anchor='right' open={props.open} onClose={props.onClose}>
			<Box sx={{ position: 'absolute', right: '80px', top: '80px' }}>
				<IconButton onClick={props.onClose}>
					<CloseIcon />
				</IconButton>
			</Box>
			<Box width='1080px' p='80px' pb='40px'>
				<Typography
					lineHeight={1}
					sx={{ ...getStyle('P_Medium'), color: getColor('Gray -2') }}
				>
					{props.productDetails?.lineOfProduct}
				</Typography>
				<Typography
					lineHeight={1}
					mt='8px'
					sx={{
						...getStyle('H11A_Medium'),
						fontSize: '30px',
						color: getColor('Gray 0'),
						maxWidth: '600px',
					}}
				>
					{props.productDetails?.title}
				</Typography>
				<Typography
					lineHeight={1}
					mt='12px'
					sx={{ ...getStyle('H14_Medium'), color: getColor('Minty +1') }}
				>
					King Size + Teal + Non-storage
				</Typography>
				<FlexBox
					height='500px'
					mt='32px'
					sx={{ position: 'relative' }}
					onMouseMove={(event: MouseEvent<HTMLImageElement>) =>
						onHoverOverImage(event)
					}
				>
					<Box
						id='glass'
						sx={{
							display: !isMagnifiedViewVisible ? 'none' : 'block',
							cursor: 'crosshair',
							width: '300px',
							height: '300px',
							position: 'absolute',
							backgroundRepeat: 'no-repeat',
							backgroundColor: 'white',
							// borderRadius: '50%',
							// backgroundImage: `url(${props.images[4].url})`,
						}}
						onMouseLeave={() => setIsMagnifiedViewVisible(false)}
					/>
					<img
						src={selectedImage?.url}
						height='500px'
						width={`auto`}
						id='image-to-zoom'
					/>
				</FlexBox>
				<FlexBox
					maxWidth={'1000px'}
					flexWrap={'wrap'}
					marginTop='32px'
					// justifyContent='center'
				>
					<ImageThumbnailSelector
						images={props.images}
						currentIndex={currentSlide}
						onImageSelection={onImageSelection}
					/>
				</FlexBox>
			</Box>
		</Drawer>
	);
};

export default ImageGalleryDrawer;
